import React, { ChangeEvent } from 'react';

import Inputs from 'components/Inputs';
import Row from 'components/Common/Row';
import Button from 'kingpin/atoms/Button';
import Typography from 'kingpin/atoms/Typography';
import FormInput from 'kingpin/forms/FormInput';
import BoardSettingsHeader from '../BoardSettingsHeader/BoardSettingsHeader';

const InheritanceHint = ({
  override,
  accountDefault,
  noun,
}: {
  override: string;
  accountDefault: string | undefined;
  noun: string;
}) => {
  const trimmed = override.trim();

  let message: string;
  if (trimmed === '') {
    message = accountDefault
      ? `Using the account default set in Advanced settings ("${accountDefault}"). Entering a value here will override it for this board.`
      : `No account default ${noun} is set in Advanced settings.`;
  } else if (trimmed === accountDefault) {
    message = 'Same as the account default set in Advanced settings.';
  } else {
    message = accountDefault
      ? `Overriding the account default ("${accountDefault}").`
      : 'Overriding the account default (none set in Advanced settings).';
  }

  return (
    <div style={{ marginTop: 4 }}>
      <Typography.Body type="Body 12" color="var(--color-text-muted)">
        {message}
      </Typography.Body>
    </div>
  );
};

const BoardDriverAppEmbedSettings = ({
  isButtonEnabled,
  setIsButtonEnabled,
  carrierSlug,
  onCarrierSlugChanged,
  programSlug,
  onProgramSlugChanged,
  accountCarrierSlug,
  accountProgramSlug,
  hasUnsavedChanges,
  handleSave,
}: {
  isButtonEnabled: boolean;
  setIsButtonEnabled: (newValue: boolean) => void;
  carrierSlug: string;
  onCarrierSlugChanged: (event: ChangeEvent<HTMLInputElement>) => void;
  programSlug: string;
  onProgramSlugChanged: (event: ChangeEvent<HTMLInputElement>) => void;
  accountCarrierSlug: string | undefined;
  accountProgramSlug: string | undefined;
  hasUnsavedChanges: boolean;
  handleSave: () => void;
}) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <BoardSettingsHeader
        title="Driver App Embed"
        description="Controls the 'View Driver App' button in this board's slideout."
      />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          overflowY: 'scroll',
          paddingRight: 8,
          paddingLeft: 8,
          borderBottom: `1px solid var(--color-grid-border)`,
        }}
      >
        <FormInput label={'Show "View Driver App" button'}>
          <Row centerAlign style={{ gap: 8 }}>
            <Inputs.Toggle
              value={isButtonEnabled}
              onChange={setIsButtonEnabled}
            />
            <Typography.Body type="Body 13" color="var(--color-text-muted)">
              {isButtonEnabled
                ? 'The button is shown in this board’s slideout.'
                : 'The button is hidden for this board.'}
            </Typography.Body>
          </Row>
        </FormInput>

        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 4 }}>
            <Typography.Body type="Label">Carrier slug</Typography.Body>
          </div>
          <Inputs.TextInput
            value={carrierSlug}
            onChange={onCarrierSlugChanged}
            placeholder={accountCarrierSlug || 'e.g. arnold_bros'}
            inputSize="Small"
            data-testid="driver-app-embed-carrier-slug"
          />
          <InheritanceHint
            override={carrierSlug}
            accountDefault={accountCarrierSlug}
            noun="carrier slug"
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 4 }}>
            <Typography.Body type="Label">Bonus slug</Typography.Body>
          </div>
          <Inputs.TextInput
            value={programSlug}
            onChange={onProgramSlugChanged}
            placeholder={accountProgramSlug || 'e.g. bonus'}
            inputSize="Small"
            data-testid="driver-app-embed-program-slug"
          />
          <InheritanceHint
            override={programSlug}
            accountDefault={accountProgramSlug}
            noun="bonus slug"
          />
        </div>
      </div>

      <Row
        spaceBetween
        centerAlign
        style={{ padding: '8px 0', height: '46px' }}
      >
        <div />
        <Button
          type="Primary"
          size="Small"
          isDisabled={!hasUnsavedChanges}
          onClick={handleSave}
          label="Save"
        />
      </Row>
    </div>
  );
};

export default BoardDriverAppEmbedSettings;
